import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HTTP_INTERCEPTORS,
  HttpContextToken,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { AuthService } from '../auth.service';
import { SocialUser } from '@abacritt/angularx-social-login';
import { UserContextService } from 'src/app/common/services/user-context.service';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';

export const SKIP_INTERCEPTOR = new HttpContextToken<boolean>(() => false);


@Injectable({
  providedIn: 'root',
})
export class AuthTokenInterceptor implements HttpInterceptor {
  currentUser: SocialUser;

  constructor (
    private authService: AuthService,
    private userContextService: UserContextService,
    private router: Router,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // add JWT to auth header.
    if (request.context.get(SKIP_INTERCEPTOR)) {
      return next.handle(request);
    }
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${this.authService.getToken()}`
      },
    });
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }

        // A 401 from the auth endpoints themselves is terminal — refreshing in response to a
        // failed refresh would loop.
        if (request.url.includes('/api/auth/')) {
          this.authService.handleUnauthorizedSession();
          return throwError(() => error);
        }

        // Access tokens are short-lived, so an expired one is the ordinary case rather than a
        // sign the session is over. Try to renew it and replay the request; only end the
        // session if the refresh token is missing, expired, or revoked. Before this, a 401
        // logged the user out immediately and the refresh path was never reached.
        return this.authService.refreshSession().pipe(
          switchMap((renewed: boolean) => {
            if (!renewed) {
              this.authService.handleUnauthorizedSession();
              return throwError(() => error);
            }

            // Not wrapped in this catchError, so a second 401 propagates instead of looping.
            return next.handle(request.clone({
              setHeaders: {
                Authorization: `Bearer ${this.authService.getToken()}`
              },
            }));
          }),
        );
      }),
    );
  }
}

export const authTokenInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthTokenInterceptor,
  multi: true,
};
